import { FC, useEffect } from 'react';
import FormGroup from 'components/form/FormGroup';
import FormRowGroup from 'components/form/FormRowGroup';
import Form from 'components/form/Form';
import { ISelectOptionProps } from 'components/form/types';
import { IDropdowns, IInputs } from '../types';

interface IProps {
  methods: any;
  handleSubmit: (data: IInputs) => void;
  btnLabel: string;
  fieldDataOptions: IDropdowns;
  canEdit?: boolean;
  isMultilingual?: boolean | null;
}

const FieldForm: FC<IProps> = ({
  methods,
  handleSubmit,
  btnLabel,
  fieldDataOptions,
  canEdit,
  isMultilingual,
}: IProps) => {
  const { getValues, watch, setValue, unregister } = methods;

  const groupByKey = (list: any[], key: string) =>
    list.reduce(
      (hash, obj) => ({
        ...hash,
        [obj[key]]: (hash[obj[key]] || []).concat(obj),
      }),
      {}
    );

  const initialValues = getValues();
  const fieldType = watch('type') as ISelectOptionProps;

  const fieldIdentifier = watch('identifier');
  const fieldHiddenIdentifier = watch('hidden_identifier');

  const type = fieldType?.value.toString() || initialValues?.type?.value.toString();

  const filteredDefaultValueOptions = type
    ? fieldDataOptions.default_options.filter(option => option.valid_for_types.includes(type))
    : fieldDataOptions.default_options;

  const groupedDefaultValueOptions = groupByKey(filteredDefaultValueOptions, 'group_label');

  const defaultValueOptions = Object.keys(groupedDefaultValueOptions).map(key => ({
    label: key,
    options: groupedDefaultValueOptions[key],
  }));

  /**
   * If field type is updated - clear the selected default value
   * because the default value options may not be valid anymore
   */
  useEffect(() => {
    if (fieldType) {
      if (fieldType.value !== 'dropdown') {
        unregister('options');
        const defaultValue = initialValues.default_value?.value;
        const isDefaultValueValid = defaultValueOptions.find(group =>
          group.options.find((opt: any) => opt?.value === defaultValue)
        );
        if (!isDefaultValueValid) setValue('default_value', null);
      } else if (fieldType.value === 'dropdown') {
        unregister('default_value');
      }

      // Clear min_length when changing away from numeric-integer
      if (fieldType.value !== 'numeric-integer') {
        unregister('min_length');
      } else if (fieldType.value === 'numeric-integer' && initialValues.min_length === undefined) {
        // Set default value of 0 for min_length when first selecting numeric-integer
        setValue('min_length', 0);
      }
    }
  }, [fieldType]);

  return (
    <Form
      methods={methods}
      onSubmit={handleSubmit}
      btnLabel={btnLabel}
      isHorizontal
      isSubmitDisabled={!canEdit}
    >
      {isMultilingual ? (
        <FormRowGroup>
          <FormGroup
            controlId="formGroupName"
            label="Име на полето"
            type="text"
            name="name"
            isRequired
            isDisabled={!canEdit}
            isRowGroup
          />
          <FormGroup
            controlId="formGroupNameEn"
            label="Field name"
            type="text"
            name="name_en"
            isDisabled={!canEdit}
            isRowGroup
          />
        </FormRowGroup>
      ) : (
        <FormGroup
          controlId="formGroupName"
          label="Име на полето"
          type="text"
          name="name"
          isRequired
          isHorizontal
          isDisabled={!canEdit}
        />
      )}
      <FormGroup
        controlId="formGroupType"
        label="Тип на полето"
        type="select"
        as="select"
        name="type"
        isRequired
        isHorizontal
        selectOptions={fieldDataOptions.types}
        isDisabled={!canEdit}
      />
      {fieldType?.value === 'dropdown' && (
        <>
          {isMultilingual ? (
            <FormRowGroup>
              <FormGroup
                controlId="formGroupOptions"
                label="Опции на списъка"
                type="select"
                as="select"
                name="options"
                hint={'За добавяне на нова опция, въведете опцията и натиснете Enter или кликнете на "Създай"'}
                isRequired
                isMulti
                isCreatable
                selectOptions={initialValues.options || []}
                isDisabled={!canEdit}
                isRowGroup
              />
              <FormGroup
                controlId="formGroupOptionsEn"
                label="List options"
                type="select"
                as="select"
                name="options_en"
                noOptionsMessage="No options"
                createMessage="Create"
                isMulti
                isCreatable
                selectOptions={initialValues.options_en || []}
                isDisabled={!canEdit}
                isRowGroup
              />
            </FormRowGroup>
          ) : (
            <FormGroup
              controlId="formGroupOptions"
              label="Опции на списъка"
              type="select"
              as="select"
              name="options"
              hint={'За добавяне на нова опция, въведете опцията и натиснете Enter или кликнете на "Създай"'}
              isRequired
              isMulti
              isCreatable
              isHorizontal
              selectOptions={initialValues.options || []}
              isDisabled={!canEdit}
            />
          )}
        </>
      )}
      <FormGroup
        controlId="formGroupDefaultValue"
        label="Предварителна стойност на полето"
        type="select"
        as="select"
        name="default_value"
        isClearable
        isHorizontal
        selectOptions={defaultValueOptions}
        isDisabled={!canEdit}
      />
      {type === 'numeric-integer' && (
        <FormGroup
          controlId="formGroupMinLength"
          label="Минимална дължина"
          hint="0 означава неограничена дължина"
          type="number"
          name="min_length"
          isNumber
          min={0}
          isHorizontal
          isDisabled={!canEdit}
        />
      )}
      <FormGroup
        controlId="formGroupMassDocBehavior"
        label="Поведение при масов документ"
        type="select"
        as="select"
        name="mass_doc_behavior"
        isRequired
        isHorizontal
        selectOptions={fieldDataOptions.mass_doc_behavior}
        isDisabled={!canEdit}
      />
      {!fieldHiddenIdentifier?.value && (
        <FormGroup
          controlId="formGroupIdentifier"
          label="Идентификатор"
          type="select"
          as="select"
          name="identifier"
          isClearable
          isHorizontal
          selectOptions={fieldDataOptions.identifiers}
          isDisabled={!canEdit}
        />
      )}
      {!fieldIdentifier?.value && (
        <FormGroup
          controlId="formGroupIdentifier"
          label="Скрит Идентификатор"
          hint="Поле със скрит идентификатор няма да бъде показано на стандартната форма за създаване на документ"
          type="select"
          as="select"
          name="hidden_identifier"
          isClearable
          isHorizontal
          selectOptions={fieldDataOptions.hidden_identifiers}
          isDisabled={!canEdit}
        />
      )}
      <hr />
      <FormGroup
        controlId="formGroupHint"
        label="Подсказка за полето"
        type="text"
        name="hint"
        isHorizontal
        isDisabled={!canEdit}
      />
      <FormGroup
        controlId="formGroupStatus"
        label="Активно"
        checkType="switch"
        name="is_active"
        isRequired
        isHorizontal
        isDisabled={!canEdit}
      />
    </Form>
  );
};

export default FieldForm;
